/* SubscriptionSection — surfaces the user's plan in Account Settings with the
 * ability to cancel (at period end) or reactivate. Reuses the existing billing
 * endpoints: /subscription/current, /subscription/cancel, /subscription/reactivate. */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { CreditCard, Loader2, AlertTriangle, RefreshCw, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_BADGE = {
  active: 'bg-emerald-100 text-emerald-700',
  cancelling: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-rose-100 text-rose-700',
  trialing: 'bg-indigo-100 text-indigo-700',
};

export const SubscriptionSection = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/subscription/current');
      setSub(r.data);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    setBusy(true);
    try {
      const r = await api.post('/subscription/cancel');
      toast.success(r.data?.message || 'Subscription will cancel at period end');
      setCancelOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
    finally { setBusy(false); }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      const r = await api.post('/subscription/reactivate');
      toast.success(r.data?.message || 'Subscription reactivated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not reactivate'); }
    finally { setBusy(false); }
  };

  if (loading) {
    return (
      <Card className="border-zinc-200"><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></CardContent></Card>
    );
  }

  const status = sub?.status || 'active';
  const tier = sub?.tier;
  const hasActive = sub?.stripe_subscription_id && status !== 'cancelled';
  const isCancelling = status === 'cancelling';
  const endDate = sub?.end_date ? new Date(sub.end_date).toLocaleDateString() : null;

  return (
    <Card className="border-zinc-200" data-testid="settings-subscription-section">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><CreditCard className="w-5 h-5 text-indigo-600" /> Subscription</CardTitle>
        <CardDescription>Manage your PostToSocial plan</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold text-zinc-900">{tier?.name || 'Free'}</span>
              <Badge className={STATUS_BADGE[status] || 'bg-zinc-100 text-zinc-600'}>{status}</Badge>
            </div>
            {endDate && (
              <p className="text-sm text-zinc-500 mt-0.5">
                {isCancelling ? 'Access until' : (tier?.is_trial ? 'Trial ends' : 'Renews')} {endDate}
              </p>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/subscription')} data-testid="view-plans-btn">
            View plans <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {isCancelling && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="cancelling-warning">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-900">Your subscription is set to cancel</p>
              <p className="text-amber-700">You'll keep access until {endDate}. Reactivate anytime before then.</p>
            </div>
          </div>
        )}

        {hasActive ? (
          isCancelling ? (
            <Button onClick={reactivate} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settings-reactivate-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />} Reactivate subscription
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setCancelOpen(true)} data-testid="settings-cancel-btn">
              Cancel subscription
            </Button>
          )
        ) : (
          <Button onClick={() => navigate('/subscription')} data-testid="settings-choose-plan-btn">
            Choose a plan
          </Button>
        )}
      </CardContent>

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="settings-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel subscription?</DialogTitle>
            <DialogDescription>
              Your plan stays active until{endDate ? ` ${endDate}` : ' the end of the current billing period'}. You won't be charged again, and you can reactivate anytime before it ends.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCancelOpen(false)}>Keep subscription</Button>
            <Button variant="destructive" onClick={cancel} disabled={busy} data-testid="confirm-cancel-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Cancel subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SubscriptionSection;
